// src/pages/program/OptionalPlayerPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { getOptionalExercises } from '../../data/optionalExercises';
import { ArrowLeft, X, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui';
import { CardioWarmup } from '../../components/testout/CardioWarmup';
import { OptionalExercisePlayer } from '../../components/optional/OptionalExercisePlayer';
import { OptionalComplete } from '../../components/optional/OptionalComplete';

const OPTIONAL_STORAGE_KEY = 'acl_care_optional_progress';

export function OptionalPlayerPage() {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const { aclCare, updateAclCare, user } = useAuth();

  const [sessionState, setSessionState] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const phaseNum = parseInt(phaseId);
  const exercises = getOptionalExercises(phaseNum);

  // Load session on mount
  useEffect(() => {
    const stored = localStorage.getItem(OPTIONAL_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.phaseNum === phaseNum) {
          setSessionState(parsed);
        } else {
          setError('Session mismatch. Please start a new session.');
        }
      } catch (e) {
        console.error('Error parsing stored optional session:', e);
        setError('Failed to load session data');
      }
    } else {
      setError('No session found. Please start from the overview.');
    }
  }, [phaseNum]);

  const saveSessionState = (updates) => {
    const newState = { ...sessionState, ...updates };
    setSessionState(newState);
    localStorage.setItem(OPTIONAL_STORAGE_KEY, JSON.stringify(newState));
  };

  // Warmup complete handler
  const handleWarmupComplete = () => {
    saveSessionState({
      warmupCompleted: true,
      phase: 'exercises',
      currentExerciseIndex: 0,
    });
  };

  // Exercise handlers
  const handleExerciseDataUpdate = (exerciseId, data) => {
    const newExerciseData = {
      ...(sessionState.exerciseData || {}),
      [exerciseId]: data,
    };
    saveSessionState({ exerciseData: newExerciseData });
  };

  const handleNavigateExercise = (index) => {
    if (index >= 0 && index < exercises.length) {
      saveSessionState({ currentExerciseIndex: index });
    }
  };

  const handleExercisesComplete = () => {
    saveSessionState({
      phase: 'complete',
      completed: true,
      completedAt: new Date().toISOString(),
    });
  };

  // Build API payload
  const buildSessionPayload = () => {
    const startTime = new Date(sessionState.startedAt);
    const endTime = sessionState.completedAt 
      ? new Date(sessionState.completedAt) 
      : new Date();
    const durationMinutes = Math.round((endTime - startTime) / 60000);

    return {
      user_id: user.id,
      phase: phaseNum,
      workout: 3, // 3 = optional
      started_at: sessionState.startedAt,
      completed_at: sessionState.completedAt || new Date().toISOString(),
      duration_minutes: durationMinutes,
      warmup_completed: sessionState.warmupCompleted,
      exercises: sessionState.exerciseData || {},
    };
  };

  // Session complete handler
  const handleSessionFinish = async () => {
    try {
      setIsSaving(true);
      
      const payload = buildSessionPayload();
      
      // Create training session record
      const result = await api.createTrainingSession(payload);

      // Update user's acl_care - increment optional completions
      const phaseKey = `phase_${phaseNum}`;
      const currentOptional = aclCare?.phases?.[phaseKey]?.completions?.optional || 0;

      const updatedAclCare = {
        ...aclCare,
        phases: {
          ...aclCare.phases,
          [phaseKey]: {
            ...aclCare.phases[phaseKey],
            completions: {
              ...aclCare.phases[phaseKey].completions,
              optional: currentOptional + 1,
            },
          },
        },
        updated_at: new Date().toISOString(),
      };

      await updateAclCare(updatedAclCare);

      // Clear session storage
      localStorage.removeItem(OPTIONAL_STORAGE_KEY);
      
      // Navigate back
      navigate(`/program/phase/${phaseId}`);
    } catch (err) {
      console.error('[OptionalPlayerPage] Error saving session:', err);
      setError('Failed to save session. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExit = () => {
    setShowExitConfirm(true);
  };

  const confirmExit = () => {
    navigate(`/program/phase/${phaseId}/optional`);
  };

  // Loading state
  if (!sessionState) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading session...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Button onClick={() => navigate(`/program/phase/${phaseId}/optional`)}>
            Back to Overview
          </Button>
        </div>
      </div>
    );
  }

  // Get header title based on phase
  const getHeaderTitle = () => {
    if (sessionState.phase === 'warmup') {
      return 'Cardio Warmup';
    }
    if (sessionState.phase === 'exercises') {
      return `Exercise ${sessionState.currentExerciseIndex + 1}/${exercises.length}`;
    }
    if (sessionState.phase === 'complete') {
      return 'Session Complete';
    }
    return 'Optional Session';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <button
          onClick={handleExit}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        
        <h1 className="font-semibold text-gray-900">
          {getHeaderTitle()}
        </h1>
        
        <button
          onClick={handleExit}
          className="p-2 -mr-2 rounded-full hover:bg-gray-100"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Cardio Warmup */}
        {sessionState.phase === 'warmup' && (
          <CardioWarmup
            onComplete={handleWarmupComplete}
            onExit={handleExit}
          />
        )}

        {/* Exercises */}
        {sessionState.phase === 'exercises' && (
          <OptionalExercisePlayer
            exercises={exercises}
            currentIndex={sessionState.currentExerciseIndex}
            exerciseData={sessionState.exerciseData || {}}
            onExerciseDataUpdate={handleExerciseDataUpdate}
            onNavigate={handleNavigateExercise}
            onComplete={handleExercisesComplete}
            onExit={handleExit}
          />
        )}

        {/* Complete */}
        {sessionState.phase === 'complete' && (
          <OptionalComplete
            phaseNum={phaseNum}
            exercises={exercises}
            exerciseData={sessionState.exerciseData || {}}
            sessionState={sessionState}
            onFinish={handleSessionFinish}
            isSaving={isSaving}
          />
        )}
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" />
          
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Exit Session?
              </h2>
              <p className="text-gray-600 mb-6">
                Your progress will be saved and you can resume later.
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowExitConfirm(false)}
                  fullWidth
                >
                  Continue Session
                </Button>
                <Button
                  onClick={confirmExit}
                  fullWidth
                >
                  Exit & Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}